import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// --- IMPORTS ---
import Layout from "../../../../Layout/Layout";
import CustomerConstants from "../../../../common/CustomerConstants";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { SearchField } from "../../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import { formatDateWithAMPM } from "../../../../common/Validations";
import {
  getEquipmentRentFormList,
  setEquipmentRentDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";
import MultiColumnFilter from "../../../../Components/MultiColumnFilter/MultiColumnFilter";
import CancelModal from "./ERFCancelModel";
import ShowStatus from "../../../../Components/Modal/supplier_app/Workflow/ShowStatus";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import { useReactToPrint } from "react-to-print";
import ErfPrintout from "./ErfPrintout";
import EarlyClosureFormPrintout from "./EarlyClosureFormPrintout";
import ClosureRefundFormPrintout from "./ClosureRefundFormPrintout";
import DeliveryChallanPrintout from "./DeliveryChallanPrintout";
import ReceiptPrintOut from "./ReceiptPrintOut";
import toast from "react-hot-toast";
import DispatchModal from "./DispatchModal";
import DispatchDetailsModal from "./DispatchDetailsModal";
import CreateServiceModal from "./CreateServiceModal";
import ViewServiceDetailsModal from "./ViewServiceDetailsModal";
import ReturnProcessModel from "./ReturnProcessModel";
import { isRentableErfType } from "./ClosureReturnSection";
import { SERVICE_ACTION_CALL_STATUS } from "./ServiceConstants";

const BOOLEAN_FILTER_KEYS = ["refundAmountIndicator", "creditNoteIndicator"];
const EXACT_MATCH_KEYS = [
  "pcCallStatus",
  "erfType",
  "privilegeCase",
  "serviceAction",
]; // exact match — no regex

const DispatchedERFList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- 1. CONFIG & STATE ---
  localStorage.setItem("menu", "Equipment Rent");
  localStorage.setItem("submenu", "Dispatched List");

  // Select Data from Redux Store
  // We use optional chaining (?.) and default values (|| {}) to prevent crashes if state is loading
  const { data: reduxData, pagination } =
    useSelector((state) => state?.customer?.equipmentRentForm) || {};
  let userData = useSelector((state) => state?.user);

  // Local State for Table UI
  // eslint-disable-next-line
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(
    CustomerConstants.ROW_REPORT_PER_PAGE[0],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [filterShow, setFilterShow] = useState(false);

  /* Cancel Modal */
  const [getCancelModalStatus, setCancelModalStatus] = useState(false);
  // eslint-disable-next-line
  const [userAllowed, setUserAllowed] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  const [showReturnProcessModel, setShowReturnProcessModel] = useState(false);

  // eslint-disable-next-line
  const cancelModalShow = () => {
    setCancelModalStatus(true);
  };
  const cancelModalHide = () => {
    setCancelModalStatus(false);
  };
  const isDispatch = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "DISPATCH") || false;
  }, [userData]);

  /* Handle selected rows */
  const [getSelectedRows, setSelectedRows] = useState([]);
  const handleRowSelected = ({ selectedRows }) => {
    const validRows = selectedRows.filter(
      (item) => item?.statusHeader !== "Cancelled",
    );
    setSelectedRows(validRows);
  };

  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const dispatchDetailedPage = (row) => {
    setSelectedRow(row);
    setShowDispatchModal(true);
  };
  // --- 1. ROLE IDENTIFICATION ---
  // eslint-disable-next-line
  const isSalesPerson = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SALES-OFFICER") || false
    );
  }, [userData]);

  // eslint-disable-next-line
  const isSalesAdmin = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SALES-ADMIN") || false
    );
  }, [userData]);
  // eslint-disable-next-line
  const isStorePerson = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "STORE") || false;
  }, [userData]);

  // eslint-disable-next-line
  const isServicePerson = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") ||
      false
    );
  }, [userData]);

  const currentUserId = userData?.data?.userId || "M000";

  const isEarlyClosureStatus = (status) => {
    return status === "Early Closure" || status === "Partial Early Closure";
  };

  // const fetchData = async (page = 1, size = 100, search = "") => {
  //   setLoading(true);
  //   try {
  //     const payload = {
  //       queryName: "DPDERF",
  //       page,
  //       per_page: size,
  //       searchTerm: search,
  //       timeZone: "Asia/Calcutta",
  //       rowFilter: {},
  //     };

  //     if (isServicePerson) {
  //       payload.rowFilter = {
  //         serviceProviderUserId: currentUserId,
  //       };
  //     }

  //     // let statusFilter = null;

  //     // if (isSalesPerson) {
  //     //   statusFilter = ["In progress", "Approved"];
  //     // } else if (isStorePerson) {
  //     //   statusFilter = ["Approved", "Dispatched"];
  //     // }

  //     // if (statusFilter) {
  //     //   payload.rowFilter = {
  //     //     $and: [{ status: { $in: statusFilter } }],
  //     //   };
  //     // }

  //     await dispatch(getEquipmentRentFormList(payload));
  //   } catch (error) {
  //     console.error("Error fetching list", error);
  //     toast.error("Error loading records");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const normalizeFilters = (filters) =>
    filters.map((condition) => {
      const key = Object.keys(condition)[0];

      // Boolean fields → convert regex string to true / false
      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        const rawValue = regexStr.replace(/\.\*/g, "").toLowerCase();
        if (rawValue === "true" || rawValue === "pending")
          return { [key]: true };
        if (rawValue === "false" || rawValue === "done")
          return { [key]: false };
        return condition;
      }

      // Exact-match fields → strip regex wrapper, send plain string value
      if (EXACT_MATCH_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        if (regexStr) {
          const rawValue = regexStr.replace(/\.\*/g, "").trim();
          return { [key]: rawValue };
        }
        return condition;
      }

      return condition;
    });

  const fetchData = useCallback(
    async (page = 1, size = 100, search = "", filters = []) => {
      setLoading(true);
      try {
        const payload = {
          queryName: "DPDERF",
          page,
          per_page: size,
          searchTerm: search,
          timeZone: "Asia/Calcutta",
          rowFilter: {},
        };

        const filterConditions = [];
        if (isServicePerson) {
          filterConditions.push({ serviceProviderCode: currentUserId });
        }
        if (filters.length > 0) {
          filterConditions.push(...filters);
        }
        if (filterConditions.length > 0) {
          payload.rowFilter = { $and: filterConditions };
        }

        await dispatch(getEquipmentRentFormList(payload));
      } catch (error) {
        console.error("Error fetching list", error);
        toast.error("Error loading records");
      } finally {
        setLoading(false);
      }
    },
    [isServicePerson, currentUserId, dispatch],
  );

  useEffect(() => {
    // Ensure we have userData before fetching to avoid empty role checks
    if (userData) {
      fetchData(currentPage, perPage, searchTxt, activeFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, userData, activeFilters, fetchData]);

  // --- HANDLERS ---
  const detailedPage = (row) => {
    // Save document number to store/local before navigating
    dispatch(setEquipmentRentDocumentNumber(row.documentNumber));
    navigate(CustomerConstants.CUSTOMER_V5_MR_ERF_DETAIL);
  };
  // eslint-disable-next-line
  const serviceDetailedPage = (row) => {
    // Save document number to store/local before navigating
    dispatch(setEquipmentRentDocumentNumber(row.documentNumber));
    navigate(CustomerConstants.CUSTOMER_V5_MR_ERF_SERVICE_PAGE);
  };

  // eslint-disable-next-line
  const handleCreate = () => {
    navigate(CustomerConstants.CUSTOMER_V5_MR_CREATE_ERF);
  };

  // const handlePageChange = (page) => {
  //   setCurrentPage(page);
  // };

  // const handlePerRowsChange = (newPerPage) => {
  //   setPerPage(newPerPage);
  //   setCurrentPage(1);
  // };
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, perPage, searchTxt);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, searchTxt);
  };

  const showStatusInfo = (rowData) => {
    setModalShowStatus(true);
    setSendDataModal(rowData);
    setModelTitle("Status Information");
  };

  const hideModalStatus = () => {
    setModalShowStatus(false);
  };

  const [modalShowStatus, setModalShowStatus] = useState(false);
  const [sendDataModal, setSendDataModal] = useState("");
  const [modelTitle, setModelTitle] = useState("");

  //  Filter configuration for Gate Entry
  const filterColumns = [
    {
      id: "erfType",
      name: "ERF Type",
      selectorKey: "erfType",
      // dropDown: true,
      //  staticOptions: ["Rent", "Sale","Sale & Rent", "Service", "Nursing Service"], // Shows InWard/OutWard
    },
    {
      id: "erfStatus",
      name: "ERF Status",
      selectorKey: "erfStatus",
    },
    {
      id: "privilegeCase",
      name: "Privilege Case",
      selectorKey: "privilegeCase",
      dropDown: true, // Shows InWard/OutWard
    },
    {
      id: "approvedBy",
      name: "Approved By",
      selectorKey: "approvedBy",
      dropDown: true,
    },
    {
      id: "documentNumber",
      name: "ERF Number",
      selectorKey: "documentNumber",
    },
    {
      id: "salePoint",
      name: "Sale Point",
      selectorKey: "salePoint",
      range: true,
    },
    {
      id: "hospital",
      name: "Hospital",
      selectorKey: "hospital",
      dropDown: true,
    },
    {
      id: "createdAt",
      name: "Created Date",
      selectorKey: "createdAt",
      month: true,
    },
    {
      id: "patientName",
      name: "Patient Name",
      selectorKey: "patientName",
    },
    {
      id: "billingAddress",
      name: "Patient Address",
      selectorKey: "billingAddress",
    },
    {
      id: "mobileNr",
      name: "Mobile Number",
      selectorKey: "mobileNr",
    },
    {
      id: "pcCallStatus",
      name: "PC Call Status",
      selectorKey: "pcCallStatus",
      dropDown: true,
      staticOptions: [
        "Extension",
        "Partial Ext",
        "Closed",
        "Follow Up",
        "Early Closure",
        "Partial Early Closure",
      ],
    },
    {
      id: "serviceAction",
      name: "Service Action",
      selectorKey: "serviceAction",
      dropDown: true,
      staticOptions: [
        "Service",
        "Replacement",
        "Service Assigned",
        "Assign To Store",
        "Service In Progress",
        "Service Completed",
        "Returning To Store",
        "Service Closed",
        "Service Cancelled",
      ],
    },
  ];

  // //  Handle multi-column filter
  // const handleMultiColumnFilter = (filterResult) => {
  //   setLoading(true);
  //   try {
  //     const filtersArray = Array.isArray(filterResult)
  //       ? filterResult
  //       : filterResult?.$and || [];

  //     const normalized = normalizeFilters(filtersArray);

  //     const obj = {
  //       queryName: "DPDERF",
  //       page: 1,
  //       per_page: 10,
  //       timeZone: "Asia/Calcutta",
  //       rowFilter: { $and: [] },
  //     };

  //     if (isServiceExecutive) {
  //       obj.rowFilter.$and.push({ serviceProviderCode: currentUserId });
  //     }

  //     if (normalized.length > 0) {
  //       obj.rowFilter.$and.push(...normalized);
  //     }

  //     if (obj.rowFilter.$and.length === 0) {
  //       delete obj.rowFilter;
  //     }

  //     // Dispatch to Redux (Thunk handles the API call)
  //     dispatch(getEquipmentRentFormList(obj));
  //   } catch (error) {
  //     console.error("Error fetching list", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // //  Handle filter cancel
  // const handleFilterCancel = () => {
  //   setFilterShow(false);
  // };

  const handleMultiColumnFilter = (filterResult) => {
    const filtersArray = Array.isArray(filterResult)
      ? filterResult
      : filterResult?.$and || [];
    const normalized = normalizeFilters(filtersArray);
    setActiveFilters(normalized);
    setCurrentPage(1);
  };

  const handleFilterCancel = () => {
    setFilterShow(false);
    setActiveFilters([]);
    setCurrentPage(1);
  };

  //create erf button
  useEffect(() => {
    if (userData?.data?.roles) {
      const allowedRoles = ["STORE"];

      const isAllowed = userData.data.roles.some((r) =>
        allowedRoles.includes(r.roleId),
      );

      setUserAllowed(isAllowed);
    }
  }, [userData]);

  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [selectedErfForService, setSelectedErfForService] = useState(null);

  const handleCreateService = (row) => {
    setSelectedErfForService(row);
    setShowCreateServiceModal(true);
  };
  const [showViewServiceModal, setShowViewServiceModal] = useState(false);
  const [viewServiceData, setViewServiceData] = useState(null);

  const handleViewServiceDetails = (row) => {
    setViewServiceData(row);
    setShowViewServiceModal(true);
  };

  const handleServiceCreated = () => {
    setShowCreateServiceModal(false);
    setSelectedErfForService(null);
    fetchData(currentPage, perPage, searchTxt);
  };

  //print
  //po print invocie
  const contentRef = useRef(null);
  const [getLoadingAdvice, setLoadingAdvice] = useState([]);
  // useReactToPrint declared at top level
  const handlePrint = useReactToPrint({
    contentRef,
    // documentTitle: `loadingAdvice_${getLoadingAdvice?.documentNumber || ""}`,
  });
  const handlePrintWithData = (row) => {
    setLoadingAdvice(row);

    // Wait for state updates before printing
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  //print for earlyclosure
  const contentRef1 = useRef(null);
  const [getEarlyClosure, setEarlyClosure] = useState(null);

  const earlyClosureHandlePrint = useReactToPrint({
    contentRef: contentRef1,
  });

  const handleEarlyClosurePrintWithData = (row) => {
    setEarlyClosure(row);
  };

  useEffect(() => {
    if (getEarlyClosure) {
      earlyClosureHandlePrint();
    }
    // eslint-disable-next-line
  }, [getEarlyClosure]);

  //print for closureRefundForm
  const contentRef2 = useRef(null);
  const [getClosureRefundForm, setClosureRefundForm] = useState(null);

  const closureRefundFormHandlePrint = useReactToPrint({
    contentRef: contentRef2,
  });

  const handleClosureRefundFormPrintWithData = (row) => {
    setClosureRefundForm(row);
  };

  useEffect(() => {
    if (getClosureRefundForm) {
      closureRefundFormHandlePrint();
    }
    // eslint-disable-next-line
  }, [getClosureRefundForm]);

  //print for deliveryChallan
  const contentRef3 = useRef(null);
  const [getdeliveryChallan, setdeliveryChallan] = useState(null);

  const deliveryChallanHandlePrint = useReactToPrint({
    contentRef: contentRef3,
  });

  const handledeliveryChallanPrintWithData = (row) => {
    setdeliveryChallan(row);
  };

  useEffect(() => {
    if (getdeliveryChallan) {
      deliveryChallanHandlePrint();
    }
    // eslint-disable-next-line
  }, [getdeliveryChallan]);

  //print for Receipt
  const contentRef4 = useRef(null);
  const [getReceipt, setReceipt] = useState(null);

  const ReceiptHandlePrint = useReactToPrint({
    contentRef: contentRef4,
  });

  // const handleReceiptPrintWithData = (row) => {
  //   setReceipt(row);
  // };
  const handleReceiptPrintWithData = (row) => {
    setReceipt(row);
    setTimeout(() => {
      ReceiptHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getReceipt) {
      ReceiptHandlePrint();
    }
    // eslint-disable-next-line
  }, [getReceipt]);
  // --- 4. COLUMNS DEFINITION (Fixed keys to match API) ---

  const userROle = JSON.parse(localStorage.getItem("userData"));
  const hasDIspatchRole = userROle?.data?.roles?.some(
    (r) => r.roleId === "DISPATCH",
  );
  const userStore = JSON.parse(localStorage.getItem("userData"));
  const hasstoreRole = userStore?.data?.roles?.some(
    (r) => r.roleId === "STORE",
  );
  // eslint-disable-next-line
  const shouldHideColumns = hasDIspatchRole || hasstoreRole;

  const [dispatchModal, setDispatchModal] = useState({
    show: false,
    data: [],
    erfData: null,
  });

  const serviceHistoryLineItemColInfo = [
    {
      name: "Version",
      cell: (row) => <span className="fw-bold">{row?.versionLabel || ""}</span>,
      width: "80px",
    },
    {
      name: "Service Provider",
      cell: (row) => (
        <div>
          <p className="fw-bold">{row?.serviceProviderName || "N/A"}</p>
          <small>{row?.serviceProviderCode || ""}</small>
        </div>
      ),
      width: "180px",
    },
    {
      name: "Category",
      cell: (row) => <span>{row?.serviceAssignCategory || "N/A"}</span>,
      width: "150px",
    },
    {
      name: "Assigned Date",
      cell: (row) => (
        <span>
          {row?.assignDateAndTime
            ? new Date(row.assignDateAndTime).toLocaleString()
            : "N/A"}
        </span>
      ),
      width: "180px",
    },
    {
      name: "Assigned By",
      cell: (row) => <span>{row?.assignedBy || "N/A"}</span>,
      width: "130px",
    },
    {
      name: "Collect Money",
      cell: (row) => (
        <span>
          {row?.shouldCollectMoney ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-secondary">No</span>
          )}
        </span>
      ),
      width: "120px",
    },
  ];

  const serviceHistoryShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(serviceHistoryLineItemColInfo);
    setModalTitle("Service History");
    setItemLevelModalShow(true);
  };

  const columns = [
    {
      name: "Created Date/Time",
      selector: (row) => row.orderDate, // MATCHED API KEY
      cell: (row) => (
        <div>
          <span className="fw-medium">{formatDateWithAMPM(row.createdAt)}</span>
        </div>
      ),
      sortable: true,
      width: "190px",
    },
    {
      name: "ERF No",
      selector: (row) => row.documentNumber, // MATCHED API KEY
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPage(row)}>
          {/* Fallback if documentNumber is missing */}
          {row.documentNumber || row.equipmentRentNr}
        </button>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "ERF Status",
      selector: (row) => row.erfStatus, // MATCHED API KEY
      cell: (row) => (
        <span>
          <TableStatusField status={row.erfStatus} />
        </span>
      ),
      width: "125px",
    },

    // {
    //   name: "Service",
    //   selector: (row) => row.documentNumber,
    //   cell: (row) => (
    //     <button
    //       className="btn btnInfo"
    //       onClick={() => serviceDetailedPage(row)}
    //       disabled={
    //         !(
    //           row.erfTypeCode === "RE" &&
    //           (row.erfStatusCode === "ASNP" || row.erfStatusCode === "DPD")
    //         )
    //       }
    //     >
    //       Service
    //     </button>
    //   ),
    //   sortable: true,
    //   width: "180px",
    // },
    //for testing
    /*  {
      name: "Invoice create",
      selector: (row) => row.erfStatus, // MATCHED API KEY
      cell: (row) => (
        <button className="btn btnTable" onClick={() => showEditInvoice(row)}>
          generate
        </button>
      ),
      width: "180px",
    }, */
    //   {
    //     name: "Invoice",
    //     cell: (row) => {
    //       const isDisabled = ["Dispatched"].includes(row?.erfStatus);

    //       return row?.invoiceCreated ? (
    //         <i
    //           className={`
    //   bi bi-receipt
    //   text-[26px]
    //   text-[#2d7a79]
    //   transition-colors duration-300
    //   cursor-pointer hover:text-[#4aa3a2]
    // `}
    //           onClick={() => showEditInvoice(row)}
    //         ></i>
    //       ) : (
    //         <button
    //           className="btn btnTable"
    //           disabled={!isDisabled}
    //           onClick={() => showEditInvoice(row)}
    //         >
    //           Generate
    //         </button>
    //       );
    //     },
    //     width: "150px",
    //     center: true,
    //     omit: !invoiceUserRoleData,
    //   },
    ...(hasDIspatchRole
      ? [
          {
            name: "Dispatch Confirm",
            // cell: (row) => {
            //   const isEnabled = row.erfStatus === "Ready To Dispatch";

            //   return (
            //     <button
            //       className="btnInfo"
            //       onClick={() => dispatchDetailedPage(row)}
            //       disabled={!isEnabled}
            //       style={{
            //         opacity: isEnabled ? 1 : 0.5,
            //         cursor: isEnabled ? "pointer" : "not-allowed",
            //       }}
            //     >
            //       Dispatch
            //     </button>
            //   );
            // },
            //            cell: (row) => {
            //   const isNR = row.erfTypeCode === "NR";
            //   const isReady = row.erfStatus === "Ready To Dispatch";

            //   if (isNR) return null;

            //   return (
            //     <button
            //       className="btnInfo"
            //       onClick={() => dispatchDetailedPage(row)}
            //       disabled={!isReady}
            //       style={{
            //         opacity: isReady ? 1 : 0.5,
            //         cursor: isReady ? "pointer" : "not-allowed",
            //       }}
            //     >
            //       Dispatch
            //     </button>
            //   );
            // },

            cell: (row) => {
              const isNR = row.erfTypeCode === "NR";
              const isReady = row.erfStatus === "Ready To Dispatch";

              // disable if NR OR not ready
              const isDisabled = isNR || !isReady;

              return (
                <button
                  className="btnInfo"
                  onClick={() => dispatchDetailedPage(row)}
                  disabled={isDisabled}
                  style={{
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  Dispatch
                </button>
              );
            },

            sortable: true,
            width: "180px",
          },
          {
            name: "Dispatch Details",
            cell: (row) => {
              const hasDispatch =
                Array.isArray(row?.dispatchDetails) &&
                row.dispatchDetails.length > 0;
              return (
                <button
                  className="btnInfo"
                  disabled={!hasDispatch}
                  onClick={() => {
                    setSelectedRow(row);
                    setDispatchModal({
                      show: true,
                      data: row?.dispatchDetails || [],
                      erfData: row,
                    });
                  }}
                  style={{
                    opacity: hasDispatch ? 1 : 0.4,
                    cursor: hasDispatch ? "pointer" : "not-allowed",
                  }}
                >
                  <i className="bi bi-truck me-1"></i>
                  {hasDispatch
                    ? `View (${row.dispatchDetails.length})`
                    : "No Data"}
                </button>
              );
            },
            width: "160px",
          },
        ]
      : []),

    {
      name: "ERF Type & Privilege Case ",
      selector: (row) => row.erfType,
      cell: (row) => (
        <div>
          <span>
            <TableStatusField status={row.erfType} />
          </span>
          <p className="fw-bold mt-1">{row.privilegeCase}</p>
        </div>
      ),
      sortable: true,
      style: { justifyContent: "left" },
      width: "210px",
    },

    {
      name: "ERF For",
      selector: (row) => row.erfForType,
      cell: (row) => (
        // <span className="fw-bold text-primary">{row.erfForType}</span>
        <span
          // className="badge"
          style={{
            color: row.erfForType === "Hospital" ? "red" : "green",
            fontWeight: "bold",
          }}
        >
          {row.erfForType}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Patient Details",
      selector: (row) => row.patientName,
      cell: (row) => (
        <div>
          <p className="fw-bold">{row?.patientName || "--"}</p>
          <p>{row?.mobileNr || "--"}</p>
          <p>{row?.billingAddress || "--"}</p>
        </div>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Amount",
      selector: (row) => row?.netAmount || 0,
      cell: (row) => (
        <div
          className="fw fw-bold"
          style={{
            color: "blue",
            fontWeight: "bold",
          }}
        >
          {`${Number(row?.netAmount || 0).toFixed(2)} INR`}
        </div>
      ),
      centre: true,
      width: "130px",
    },
    {
      name: "PC Call Status",
      id: "pcCallStatus",
      dropDown: true,
      selector: (row) => row.pcCallStatus, // MATCHED API KEY
      cell: (row) => <TableStatusField status={row.pcCallStatus} />,
      width: "150px",
    },
    {
      name: "Return Process",
      cell: (row) => {
        // Type gate — only rentable ERFs have items to physically collect
        if (!isRentableErfType(row?.erfTypeCode)) {
          return <span className="text-muted small">--</span>;
        }

        // Status gate — only closure-related statuses trigger the return flow
        const isPartialExt =
          row.pcCallStatus === "Partial Ext" || row.pcCallStatusCode === "PEXT";
        const isClosed =
          row.pcCallStatus === "Closed" || row.pcCallStatusCode === "CD";
        const isEarlyClosure =
          row.pcCallStatus === "Early Closure" ||
          row.pcCallStatusCode === "ELCS";
        const isPartialEarlyClosure =
          row.pcCallStatus === "Partial Early Closure" ||
          row.pcCallStatusCode === "PELCS";

        if (
          isPartialExt ||
          isClosed ||
          isEarlyClosure ||
          isPartialEarlyClosure
        ) {
          return (
            <button className="btnInfo" onClick={() => returnProcessModel(row)}>
              <i className="bi bi-arrow-return-left me-1"></i>
              Return Process
            </button>
          );
        }
        return <span className="text-muted small">--</span>;
      },
      sortable: true,
      // center: true,
      style: { justifyContent: "center" },
      width: "180px",
    },
    //   {
    //   name: "Create Service",
    //   cell: (row) => (
    //     <>
    //       {["Service", "Service Completed", "Service Cancelled"].includes(row?.pcCallStatus) && (
    //         <button
    //           className="btnInfo"
    //           onClick={() => handleCreateService(row)}
    //         >
    //           <i className="bi bi-plus-circle"></i> Create Service
    //         </button>
    //       )}
    //     </>
    //   ),
    //   width: "150px"
    // },
    {
      name: "Sale Point",
      selector: (row) => row.salePoint,
      cell: (row) => (
        <div>
          <p>{row.salePoint}</p>
          <p className="fw fw-bold">{row.createdBy}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },
    {
      name: "Updated Remarks",
      cell: (row) => {
        const version = row?.version || "V0";
        const count = row?.updateRemarks?.length || 0;

        return (
          <>
            <span
              style={{ marginLeft: "12px" }}
              onClick={() => remarkItemShowModal(row?.updateRemarks || [])}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>

            <span className="suppliersIcon">{`${count}/${version}`}</span>
          </>
        );
      },
      width: "160px",
    },
    {
      name: "Service Action",
      id: "serviceAction",
      selector: (row) => row.serviceAction,
      cell: (row) =>
        row?.serviceAction ? (
          <TableStatusField status={row.serviceAction} />
        ) : (
          <span className="text-muted small">—</span>
        ),
      width: "170px",
    },
    ...(isDispatch
      ? [
          {
            name: "Create Service",
            cell: (row) => (
              <>
                {[
                  "Service",
                  "Replacement",
                  "Service Completed",
                  "Service Cancelled",
                ].includes(row?.serviceAction) && (
                  <button
                    className="btnInfo"
                    onClick={() => handleCreateService(row)}
                  >
                    <i className="bi bi-plus-circle"></i> Create Service
                  </button>
                )}
              </>
            ),
            width: "150px",
          },
        ]
      : []),
    {
      name: "View Updated Service",
      cell: (row) => {
        // ── Gate: only show when a service record has been created ──────────────
        // SERVICE_ACTION_CALL_STATUS values are set AFTER CreateServiceModal runs.
        // Plain "Service" / "Replacement" are just dispatch triggers — no record yet.
        const activeStatuses = Object.values(SERVICE_ACTION_CALL_STATUS);
        const hasServiceRecord =
          activeStatuses.includes(row?.serviceAction) || !!row?.latestServiceNr; // fallback: explicit service number on row

        if (!hasServiceRecord) {
          return <span className="text-muted small">—</span>;
        }

        return (
          <button
            className="btnInfo"
            onClick={() => handleViewServiceDetails(row)}
          >
            <i className="bi bi-eye me-1" /> View Details
          </button>
        );
      },
      width: "160px",
    },
    {
      name: "Service Executives",
      cell: (row) => {
        const serviceExecutives = row?.assignedServiceExecutives || [];
        const count = serviceExecutives.length;
        return (
          <>
            <span
              style={{
                marginLeft: "12px",
                cursor: count > 0 ? "pointer" : "not-allowed",
                opacity: count > 0 ? 1 : 0.5,
              }}
              onClick={() => {
                if (count > 0) {
                  serviceHistoryShowModal(
                    serviceExecutives.map((item, index) => ({
                      ...item,
                      versionLabel: `V${index + 1}`,
                    })),
                  );
                }
              }}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>
            <span className="suppliersIcon">{`${count} Assigned`}</span>
          </>
        );
      },
      width: "200px",
    },

    ...(reduxData?.some(
      (row) =>
        Array.isArray(row?.workflowActionLogs) &&
        row.workflowActionLogs.length > 0,
    )
      ? [
          {
            name: "Workflow log",
            selector: (row) => row.workflowActionLogs,
            cell: (row) => {
              const hasLogs =
                Array.isArray(row?.workflowActionLogs) &&
                row.workflowActionLogs.length > 0;

              return (
                <div
                  style={{ width: "100%" }}
                  className="d-flex justify-content-center iconTable"
                >
                  <img
                    src="assets/img/newIcons/workflow.png"
                    alt="workflow"
                    style={{
                      width: "35px",
                      opacity: hasLogs ? 1 : 0.4,
                      cursor: hasLogs ? "pointer" : "not-allowed",
                    }}
                    className="mt-1"
                    onClick={
                      hasLogs
                        ? () => showStatusInfo(row.workflowActionLogs)
                        : undefined
                    }
                  />
                </div>
              );
            },
            sortable: true,
            width: "150px",
          },
        ]
      : []),

    {
      name: "WorkFlow Status",
      selector: (row) => row.status, // MATCHED API KEY
      cell: (row) => (
        <span>
          <TableStatusField status={row.status} />
        </span>
      ),
      width: "160px",
    },
    {
      name: "Receipt",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handleReceiptPrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Agremnent",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handlePrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "130px",
    },

    {
      name: "Early Closure Form",
      selector: (row) => row.attachments,
      cell: (row) =>
        isEarlyClosureStatus(row.pcCallStatus) ? (
          <div
            onClick={() => handleEarlyClosurePrintWithData(row)}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
          >
            <i className="bi bi-printer text-lg fs-5"></i>
          </div>
        ) : (
          "--" //  hide when not matching
        ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Closure Refund Form",
      selector: (row) => row.attachments,
      cell: (row) =>
        isEarlyClosureStatus(row.pcCallStatus) ? (
          <div
            onClick={() => handleClosureRefundFormPrintWithData(row)}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
          >
            <i className="bi bi-printer text-lg fs-5"></i>
          </div>
        ) : (
          "--" //  hide when not matching
        ),
      sortable: true,
      width: "180px",
    },

    {
      name: "Delivery Challan",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handledeliveryChallanPrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    // ...(!shouldHideColumns
    //   ? [

    //     ]
    //   : []),
    ...(!hasDIspatchRole
      ? [
          {
            name: "Dispatch Details",
            cell: (row) => {
              const hasDispatch =
                Array.isArray(row?.dispatchDetails) &&
                row.dispatchDetails.length > 0;
              return (
                <button
                  className="btnInfo"
                  disabled={!hasDispatch}
                  onClick={() => {
                    setSelectedRow(row);
                    setDispatchModal({
                      show: true,
                      data: row?.dispatchDetails || [],
                      erfData: row,
                    });
                  }}
                  style={{
                    opacity: hasDispatch ? 1 : 0.4,
                    cursor: hasDispatch ? "pointer" : "not-allowed",
                  }}
                >
                  <i className="bi bi-truck me-1"></i>
                  {hasDispatch
                    ? `View (${row.dispatchDetails.length})`
                    : "No Data"}
                </button>
              );
            },
            width: "160px",
          },
        ]
      : []),
  ];

  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""} </span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""} </span>,
      width: "180px",
    },
  ];
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);

  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("ERF Remarks / Version");
    setItemLevelModalShow(true);
  };
  const itemLevelHideModal = () => {
    setItemLevelModalShow(false);
  };

  const returnProcessModel = (row) => {
    setSelectedRow(row);
    setShowReturnProcessModel(true);
  };
  // eslint-disable-next-line
  const isDispatchManager = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "DISPATCH") || false;
  }, [userData]);
  // eslint-disable-next-line
  const isServiceExecutive = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") ||
      false
    );
  }, [userData]);

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            {/* --- Header Section --- */}
            <Row>
              <Col xs={12} md={8} lg={8}>
                <h2 className="mainTitle">ERF - Dispatched List</h2>
                <p className="subText">View Details of Dispatched ERFs</p>
              </Col>

              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchTxt}
                  onChange={(e) => setSearchTxt(e.target.value)}
                  onClick={() => fetchData(1, perPage, searchTxt)}
                  placeholder="Search..."
                />
              </Col>

              <Col xs={4} md={1} lg={1}>
                <span
                  className="filterIcon"
                  onClick={() => setFilterShow(!filterShow)}
                >
                  <i className="bi bi-filter"></i>
                </span>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col xs={12} md={12} lg={12}>
                {filterShow && (
                  <MultiColumnFilter
                    columns={filterColumns}
                    data={reduxData}
                    onFilter={handleMultiColumnFilter}
                    onCancel={handleFilterCancel}
                    output="and"
                  />
                )}
              </Col>
            </Row>

            {/* --- Table Accordion --- */}
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-down" : "bi-chevron-right"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">RL</span>
                    </Col>
                    <Col xs={6} md={4} lg={4}>
                      <span className="headerTitle">ERF - Dispatched List</span>
                      <span className="subHeaderTitle">
                        View Details and Status
                      </span>
                    </Col>

                    {/* Create Button in Accordion Header */}
                    {/* <Col xs={12} md={8} lg={8} className="headerButtonAlign">
                      {(isSalesPerson || isSalesAdmin) && (
                        <Col xs={4} md={2} lg={2} className="ms-auto">
                          <button className="btnTable" onClick={handleCreate}>
                            <i className="bi bi-plus-lg mx-2"></i> Create ERF
                          </button>
                        </Col>
                      )}

                      <Col xs={3} md={2} lg={2} className="">
                        <button
                          className="btnTable"
                          onClick={cancelModalShow}
                          disabled={getSelectedRows.length === 1 ? false : true}
                        >
                          <i className="bi bi-x-circle"></i> Cancel
                        </button>
                      </Col>
                    </Col> */}
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12}>
                        {/* <Loader isLoading={loading || status} /> */}
                        <DataTable
                          title=""
                          columns={columns}
                          // Directly pass the data array from Redux
                          data={reduxData || []}
                          pagination
                          paginationServer
                          // Pass total rows from Redux pagination
                          paginationTotalRows={pagination?.total || 0}
                          paginationRowsPerPageOptions={
                            CustomerConstants.ROW_REPORT_PER_PAGE
                          }
                          // Handlers
                          paginationPerPage={perPage}
                          paginationDefaultPage={currentPage}
                          onChangePage={handlePageChange}
                          onChangeRowsPerPage={handlePerRowsChange}
                          selectableRows
                          highlightOnHover
                          pointerOnHover
                          persistTableHead={false}
                          onSelectedRowsChange={handleRowSelected}
                        />
                      </Col>
                    </Row>
                    <div style={{ display: "none" }}>
                      <ErfPrintout ref={contentRef} data={getLoadingAdvice} />
                    </div>
                    <div style={{ display: "none" }}>
                      <EarlyClosureFormPrintout
                        ref={contentRef1}
                        data={getEarlyClosure}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ClosureRefundFormPrintout
                        ref={contentRef2}
                        data={getClosureRefundForm}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <DeliveryChallanPrintout
                        ref={contentRef3}
                        data={getdeliveryChallan}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ReceiptPrintOut
                        key={
                          getReceipt?.documentNumber || "Receipt-Printout-key"
                        }
                        ref={contentRef4}
                        data={getReceipt}
                        isExtension={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <CancelModal
        title="Cancel the ERF Form"
        show={getCancelModalStatus}
        onHide={cancelModalHide}
        selectedrow={getSelectedRows}
      />
      <ShowStatus
        show={modalShowStatus}
        onHide={() => hideModalStatus()}
        rowdatamodal={sendDataModal}
        modeltitle={modelTitle}
      />

      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />
      <DispatchModal
        key={`dispatch-${selectedRow?.documentNumber || "empty"}`}
        show={showDispatchModal}
        onHide={() => {
          setShowDispatchModal(false);
          setSelectedRow(null);
        }}
        erfData={selectedRow}
      />
      <DispatchDetailsModal
        key={`details-${selectedRow?.documentNumber || "empty"}`}
        show={dispatchModal.show}
        onHide={() =>
          setDispatchModal({ show: false, data: [], erfData: null })
        }
        dispatchDetails={dispatchModal.data}
        erfData={dispatchModal.erfData}
      />

      <CreateServiceModal
        key={`createService-${selectedErfForService?.documentNumber || "createService-empty"}`}
        show={showCreateServiceModal}
        onHide={() => setShowCreateServiceModal(false)}
        erfData={selectedErfForService}
        onSuccess={handleServiceCreated}
      />
      <ViewServiceDetailsModal
        key={`viewService-${viewServiceData?.documentNumber || "viewService-empty"}`}
        show={showViewServiceModal}
        onHide={() => setShowViewServiceModal(false)}
        data={viewServiceData}
      />
      <ReturnProcessModel
        key={`return-${selectedRow?.documentNumber || "empty"}`}
        show={showReturnProcessModel}
        onHide={() => setShowReturnProcessModel(false)}
        // erfData={selectedRow}
        documentNumber={selectedRow?.documentNumber}
        isExtension={false}
      />
    </main>
  );
};

export default DispatchedERFList;
